import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "features/store";
import SetEmailModal, { wasSetEmailDismissed } from "../SetEmailModal";

type ProtectedRouteProps = {
  children: JSX.Element;
};

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const [emailDismissed, setEmailDismissed] = useState(wasSetEmailDismissed);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isAuthenticated && user && !user.is_pass_reset) {
    return <Navigate to="/reset-password" replace />;
  }

  const needsEmail = Boolean(
    isAuthenticated &&
    user &&
    user.is_pass_reset &&
    !(user.email ?? "").trim() &&
    !emailDismissed,
  );

  const pp = user?.platform_profile;

  if (pp?.is_archived) {
    return <Navigate to="/login" replace />;
  }

  if (location.pathname === "/chat") {
    const canChat = pp?.is_ai_agent_user && !pp?.is_partnership_team;
    if (user && !canChat) {
      return <Navigate to="/" replace />;
    }
  }

  if (location.pathname === "/data-storage") {
    const canStorage =
      !pp?.is_partnership_team &&
      (pp?.is_data_storage_user ||
        pp?.is_superuser ||
        pp?.is_calibrate_manager ||
        pp?.user_type === "internal");
    if (user && !canStorage) {
      return <Navigate to="/" replace />;
    }
  }

  if (location.pathname === "/secure-file-upload") {
    const canSecureUpload =
      pp?.is_secure_file_upload_user ||
      pp?.is_superuser ||
      pp?.is_calibrate_manager ||
      pp?.user_type === "internal";
    if (user && !canSecureUpload) {
      return <Navigate to="/" replace />;
    }
  }

  return (
    <>
      {children}
      {needsEmail && (
        <SetEmailModal open={true} onClose={() => setEmailDismissed(true)} />
      )}
    </>
  );
};

export default ProtectedRoute;
